import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Link,
  InputAdornment,
  IconButton,
  AppBar,
  Toolbar,
  Grow,
  Fade,
  Slide,
  Zoom,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Person,
  ArrowBack,
  Refresh,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/index'
import { login, setLoading, setError } from '@store/slices/authSlice'
import { addNotification } from '@store/slices/appSlice'
import { AuthService } from '@services/authService'
import {
  LoginCredentials,
  User,
  UserRole,
  OtpVerificationResponse,
  AuthResponse,
} from '@/types'
import { createValidationRules, validateForm } from '@utils/validation'
import SignupHeader from './SignupHeader'

const PatientLoginPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { error, isLoading } = useAppSelector(state => state.auth)

  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // OTP Verification State
  const [showOtpForm, setShowOtpForm] = useState(false)
  const [otpEmail, setOtpEmail] = useState('')
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [isResendingOtp, setIsResendingOtp] = useState(false)
  const [resendTimer, setResendTimer] = useState(0)
  const [canResend, setCanResend] = useState(false)
  const [otpError, setOtpError] = useState<string | null>(null)

  // Refs for OTP input fields
  const otpRefs = useRef<(HTMLInputElement | null)[]>([])

  const from = (location.state as any)?.from?.pathname || '/dashboard'

  const validationRules = {
    email: [
      createValidationRules.required('Email is required'),
      createValidationRules.email(),
    ],
    password: [createValidationRules.required('Password is required')],
  }

  const handleInputChange =
    (field: keyof LoginCredentials) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({
        ...prev,
        [field]: event.target.value,
      }))

      // Clear field error when user starts typing
      if (formErrors[field]) {
        setFormErrors(prev => ({
          ...prev,
          [field]: '',
        }))
      }
    }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    // Validate form
    const errors = validateForm(formData, validationRules)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    dispatch(setLoading(true))
    dispatch(setError(null))
    setOtpError(null)

    try {
      const response = await AuthService.patientLogin(formData)

      // Check if OTP verification is required
      if ('step' in response && response.step === 'OTP_VERIFICATION_REQUIRED') {
        const otpResponse = response as OtpVerificationResponse
        setOtpEmail(otpResponse.email)
        setShowOtpForm(true)
        setResendTimer(120) // 2 minutes
        setCanResend(false)
        setOtp(['', '', '', '', '', ''])
        dispatch(setLoading(false))
        return
      }

      // If no OTP required, proceed with normal login
      const authResponse = response as AuthResponse
      dispatch(
        login({
          user: authResponse.user,
          token: authResponse.token,
          refreshToken: authResponse.refreshToken,
        })
      )

      // Show success notification
      dispatch(
        addNotification({
          type: 'success',
          title: 'Welcome back!',
          message: `Hello ${authResponse.user.firstName}, you have successfully logged in.`,
        })
      )

      navigate(from, { replace: true })
    } catch (error: any) {
      console.error('Login failed:', error)

      let errorMessage = 'Login failed. Please try again.'

      if (error.response?.status === 401) {
        errorMessage =
          'Invalid email or password. Please check your credentials.'
      } else if (error.response?.status === 404) {
        errorMessage =
          'Account not found. Please check your email or create a new account.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.request) {
        errorMessage =
          'Unable to connect to server. Please check your internet connection.'
      }

      dispatch(setError(errorMessage))

      dispatch(
        addNotification({
          type: 'error',
          title: 'Login Failed',
          message: errorMessage,
        })
      )
    } finally {
      dispatch(setLoading(false))
    }
  }

  const handleCreateAccountClick = () => {
    navigate('/patient-create-account')
  }

  const handleForgotPasswordClick = () => {
    navigate('/forgot-password', { state: { from: '/patient-login' } })
  }

  // Timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [resendTimer])

  // Focus first OTP input when OTP form is shown
  useEffect(() => {
    if (showOtpForm) {
      setTimeout(() => {
        otpRefs.current[0]?.focus()
      }, 100)
    }
  }, [showOtpForm])

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return // Only allow single digit

    const newOtp = [...otp]
    newOtp[index] = value.replace(/\D/g, '') // Only allow digits
    setOtp(newOtp)
    setOtpError(null)

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }

    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6)
        const newOtp = [...otp]
        for (let i = 0; i < 6; i++) {
          newOtp[i] = digits[i] || ''
        }
        setOtp(newOtp)

        // Focus the last filled input or the next empty one
        const lastFilledIndex = Math.min(digits.length - 1, 5)
        otpRefs.current[lastFilledIndex]?.focus()
      })
    }
  }

  const handleVerifyOtp = useCallback(async () => {
    const otpValue = otp.join('')

    if (otpValue.length !== 6) {
      const errorMessage = 'Please enter the complete 6-digit OTP.'
      setOtpError(errorMessage)
      return
    }

    setIsVerifyingOtp(true)
    setOtpError(null)

    try {
      const response = await AuthService.verifyPatientOtp({
        email: otpEmail,
        otp: otpValue,
      })

      dispatch(
        login({
          user: response.user,
          token: response.token,
          refreshToken: response.refreshToken,
        })
      )

      dispatch(
        addNotification({
          type: 'success',
          title: 'Welcome back!',
          message: `Hello ${response.user.firstName}, you have successfully logged in.`,
        })
      )

      navigate(from, { replace: true })
    } catch (error: any) {
      console.error('OTP verification failed:', error)
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Invalid OTP. Please try again.'
      setOtpError(errorMessage)
      // Clear OTP on error and focus first input
      setOtp(['', '', '', '', '', ''])
      setTimeout(() => {
        otpRefs.current[0]?.focus()
      }, 100)
    } finally {
      setIsVerifyingOtp(false)
    }
  }, [otp, otpEmail, dispatch, navigate, from])

  const handleResendOtp = useCallback(async () => {
    if (!canResend) return

    setIsResendingOtp(true)
    setOtpError(null)

    try {
      await AuthService.resendPatientOtp(otpEmail)
      dispatch(
        addNotification({
          type: 'success',
          title: 'OTP Resent',
          message: 'A new OTP has been sent to your email.',
        })
      )
      setResendTimer(120) // Reset timer to 2 minutes
      setCanResend(false)
      setOtp(['', '', '', '', '', ''])
      setTimeout(() => {
        otpRefs.current[0]?.focus()
      }, 100)
    } catch (error: any) {
      console.error('Resend OTP failed:', error)
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to resend OTP. Please try again.'
      setOtpError(errorMessage)
    } finally {
      setIsResendingOtp(false)
    }
  }, [otpEmail, canResend, dispatch])

  const handleBackToLogin = () => {
    setShowOtpForm(false)
    setOtp(['', '', '', '', '', ''])
    setOtpError(null)
    setResendTimer(0)
    setCanResend(false)
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const isOtpComplete = otp.every(digit => digit !== '')

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <SignupHeader />

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left Section - Image */}
        <Zoom in timeout={600} style={{ transitionDelay: '150ms' }}>
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              display: { xs: 'none', md: 'block' },
              overflow: 'hidden',
              bgcolor: 'grey.50',
              paddingLeft: '6rem',
            }}
          >
            <Box
              component="img"
              src="/PatientLoginPoster.png"
              alt="Patient login illustration"
              sx={{
                padding: '8%',
                width: '90vw',
                height: '90vh',
                objectFit: 'cover',
                objectPosition: 'center',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                },
              }}
            />
          </Box>
        </Zoom>

        {/* Right Section - Login Form */}
        <Slide
          direction="left"
          in
          timeout={500}
          style={{ transitionDelay: '250ms' }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              px: { xs: 3, sm: 4, md: 6, lg: 8 },
              py: { xs: 4, sm: 6 },
              bgcolor: 'grey.50',
            }}
          >
            {/* Login Form Card */}
            <Grow in timeout={600} style={{ transitionDelay: '350ms' }}>
              <Paper
                elevation={2}
                sx={{
                  p: { xs: 4, sm: 5, md: 6 },
                  borderRadius: 4,
                  width: '100%',
                  maxWidth: { xs: '100%', sm: '450px', md: '500px' },
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {showOtpForm ? (
                  /* OTP Verification Form */
                  <>
                    {/* Back Button */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        mb: 2,
                      }}
                    >
                      <IconButton
                        onClick={handleBackToLogin}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            color: '#3BAE78',
                            backgroundColor: 'rgba(59, 174, 120, 0.1)',
                          },
                        }}
                      >
                        <ArrowBack />
                      </IconButton>
                    </Box>

                    {/* Header */}
                    <Fade in timeout={500} style={{ transitionDelay: '450ms' }}>
                      <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 400,
                            color: 'grey.800',
                            mb: 1,
                            fontSize: {
                              xs: '1.75rem',
                              sm: '2rem',
                              md: '2.25rem',
                            },
                          }}
                        >
                          Verify OTP
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'grey.600',
                            mb: 1,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          }}
                        >
                          We've sent a 6-digit verification code to
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{
                            color: '#3BAE78',
                            mb: 1,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          }}
                        >
                          {otpEmail}
                        </Typography>
                      </Box>
                    </Fade>

                    {/* OTP Form */}
                    <Grow in timeout={400}>
                      <Box
                        component="form"
                        onSubmit={e => {
                          e.preventDefault()
                          handleVerifyOtp()
                        }}
                      >
                        {/* OTP Input Fields */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: { xs: 1, sm: 2 },
                            mb: 3,
                          }}
                        >
                          {otp.map((digit, index) => (
                            <TextField
                              key={index}
                              inputRef={el => (otpRefs.current[index] = el)}
                              value={digit}
                              onChange={e =>
                                handleOtpChange(index, e.target.value)
                              }
                              onKeyDown={e => handleKeyDown(index, e)}
                              variant="outlined"
                              inputProps={{
                                maxLength: 1,
                                style: {
                                  textAlign: 'center',
                                  fontSize: '1.5rem',
                                  fontWeight: 600,
                                  padding: '16px 0',
                                },
                              }}
                              sx={{
                                width: { xs: 45, sm: 56 },
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  bgcolor: 'white',
                                  '&.Mui-focused': {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: '#3BAE78',
                                      borderWidth: 2,
                                    },
                                  },
                                },
                              }}
                            />
                          ))}
                        </Box>

                        {/* Error Alert */}
                        {otpError && (
                          <Fade in timeout={400}>
                            <Box sx={{ mb: 2 }}>
                              <Alert severity="error" variant="filled">
                                {otpError}
                              </Alert>
                            </Box>
                          </Fade>
                        )}

                        {/* Verify Button */}
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          disabled={isVerifyingOtp || !isOtpComplete}
                          onClick={handleVerifyOtp}
                          sx={{
                            mt: 2,
                            mb: 3,
                            textTransform: 'none',
                            borderRadius: 2,
                            bgcolor: '#3BAE78',
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            transition:
                              'transform 120ms ease, box-shadow 120ms ease',
                            '&:hover': {
                              transform: 'scale(1.02)',
                              bgcolor: '#2d8c5f',
                              boxShadow: '0 8px 16px rgba(59, 174, 120, 0.25)',
                            },
                            '&:active': {
                              transform: 'scale(0.98)',
                            },
                            '&:disabled': {
                              bgcolor: 'grey.300',
                              color: 'grey.500',
                              opacity: 0.6,
                              cursor: 'not-allowed',
                            },
                          }}
                        >
                          {isVerifyingOtp ? (
                            <>
                              <CircularProgress
                                size={22}
                                sx={{ color: 'white', mr: 1 }}
                              />
                              Verifying...
                            </>
                          ) : (
                            'Verify & Continue'
                          )}
                        </Button>

                        {/* Resend Section */}
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', mb: 1 }}
                          >
                            Didn't receive the code?
                          </Typography>
                          {canResend ? (
                            <Button
                              variant="text"
                              onClick={handleResendOtp}
                              disabled={isResendingOtp}
                              startIcon={<Refresh />}
                              sx={{
                                color: '#3BAE78',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                  backgroundColor: 'rgba(59, 174, 120, 0.1)',
                                },
                              }}
                            >
                              {isResendingOtp ? 'Resending...' : 'Resend OTP'}
                            </Button>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary' }}
                            >
                              Resend available in {formatTime(resendTimer)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grow>
                  </>
                ) : (
                  /* Login Form */
                  <>
                    {/* Header */}
                    <Fade in timeout={500} style={{ transitionDelay: '450ms' }}>
                      <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 400,
                            color: 'grey.800',
                            mb: 1,
                            fontSize: {
                              xs: '1.75rem',
                              sm: '2rem',
                              md: '2.25rem',
                            },
                          }}
                        >
                          Welcome back!
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: 'grey.600',
                            fontWeight: 400,
                            fontSize: { xs: '0.8rem', sm: '1rem' },
                          }}
                        >
                          Enter your Credentials to access your Patient Portal
                        </Typography>
                      </Box>
                    </Fade>

                    {/* Error Display */}
                    {error && (
                      <Fade
                        in
                        timeout={400}
                        style={{ transitionDelay: '500ms' }}
                      >
                        <Alert severity="error" sx={{ mb: 3 }}>
                          {error}
                        </Alert>
                      </Fade>
                    )}

                    {/* Login Form */}
                    <Box component="form" onSubmit={handleSubmit}>
                      {/* Email Field */}
                      <Fade
                        in
                        timeout={400}
                        style={{ transitionDelay: '550ms' }}
                      >
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'grey.700',
                              mb: 1,
                              fontWeight: 500,
                              fontSize: '0.875rem',
                            }}
                          >
                            Email
                          </Typography>
                          <TextField
                            fullWidth
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange('email')}
                            placeholder="Enter email"
                            required
                            error={!!formErrors.email}
                            helperText={formErrors.email}
                            disabled={isLoading}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'white',
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '& fieldset': {
                                  borderColor: 'grey.300',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'grey.400',
                                  transform: 'scale(1.02)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#3BAE78',
                                  borderWidth: 2,
                                },
                              },
                              '& .MuiInputBase-input': {
                                py: 1.5,
                                fontSize: '0.875rem',
                              },
                            }}
                          />
                        </Box>
                      </Fade>

                      {/* Password Field */}
                      <Fade
                        in
                        timeout={400}
                        style={{ transitionDelay: '650ms' }}
                      >
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'grey.700',
                              mb: 1,
                              fontWeight: 500,
                              fontSize: '0.875rem',
                            }}
                          >
                            Password
                          </Typography>
                          <TextField
                            fullWidth
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={handleInputChange('password')}
                            placeholder="Enter password"
                            required
                            error={!!formErrors.password}
                            helperText={formErrors.password}
                            disabled={isLoading}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'white',
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '& fieldset': {
                                  borderColor: 'grey.300',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'grey.400',
                                  transform: 'scale(1.02)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#3BAE78',
                                  borderWidth: 2,
                                },
                              },
                              '& .MuiInputBase-input': {
                                py: 1.5,
                                fontSize: '0.875rem',
                              },
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() =>
                                      setShowPassword(!showPassword)
                                    }
                                    edge="end"
                                    disabled={isLoading}
                                    sx={{
                                      color: 'grey.400',
                                      transition: 'transform 0.2s ease',
                                      '&:hover': {
                                        transform: 'scale(1.1)',
                                      },
                                    }}
                                  >
                                    {showPassword ? (
                                      <VisibilityOff />
                                    ) : (
                                      <Visibility />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                      </Fade>

                      {/* Forgot Password Link */}
                      <Fade
                        in
                        timeout={400}
                        style={{ transitionDelay: '750ms' }}
                      >
                        <Box sx={{ textAlign: 'left', mb: 4 }}>
                          <Link
                            component="button"
                            type="button"
                            onClick={handleForgotPasswordClick}
                            sx={{
                              color: '#3BAE78',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                textDecoration: 'underline',
                                transform: 'translateX(2px)',
                              },
                            }}
                          >
                            Forgot password
                          </Link>
                        </Box>
                      </Fade>

                      {/* Login Button */}
                      <Fade
                        in
                        timeout={400}
                        style={{ transitionDelay: '850ms' }}
                      >
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          disabled={isLoading}
                          sx={{
                            bgcolor: '#3BAE78',
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            mb: 4,
                            textTransform: 'none',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              bgcolor: '#2d8c5f',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(46, 125, 175, 0.3)',
                            },
                            '&:disabled': {
                              bgcolor: 'grey.300',
                              color: 'grey.500',
                            },
                          }}
                        >
                          {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            'Login'
                          )}
                        </Button>
                      </Fade>
                    </Box>

                    {/* Create Account Link */}
                    <Fade in timeout={400} style={{ transitionDelay: '850ms' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'grey.600',
                            fontSize: '0.875rem',
                          }}
                        >
                          New to PillarThera?{' '}
                          <Link
                            component="button"
                            type="button"
                            onClick={handleCreateAccountClick}
                            sx={{
                              color: '#3BAE78',
                              textDecoration: 'none',
                              fontWeight: 600,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                textDecoration: 'underline',
                                transform: 'translateY(-1px)',
                              },
                            }}
                          >
                            Create an account
                          </Link>
                        </Typography>
                      </Box>
                    </Fade>
                  </>
                )}
              </Paper>
            </Grow>
          </Box>
        </Slide>
      </Box>
    </Box>
  )
}

export default PatientLoginPage

// import React, { useState } from 'react'
// import {
//   Box,
//   Typography,
//   Button,
//   TextField,
//   Paper,
//   Link,
//   InputAdornment,
//   IconButton,
//   AppBar,
//   Toolbar,
//   Grow,
//   Fade,
//   Slide,
//   Zoom,
//   Alert,
// } from '@mui/material'
// import { Visibility, VisibilityOff, Person } from '@mui/icons-material'
// import { useNavigate, useLocation } from 'react-router-dom'
// import { useAppDispatch, useAppSelector } from '@store/index'
// import { login, setLoading, setError } from '@store/slices/authSlice'
// import { addNotification } from '@store/slices/appSlice'
// import { MockAuthService } from '@services/mockAuthService'
// import { LoginCredentials } from '../types/index'
// import { createValidationRules, validateForm } from '@utils/validation'
// import SignupHeader from './SignupHeader'

// const PatientLoginPage: React.FC = () => {
//   const navigate = useNavigate()
//   const location = useLocation()
//   const dispatch = useAppDispatch()
//   const { error, isLoading } = useAppSelector(state => state.auth)

//   const [showPassword, setShowPassword] = useState(false)
//   const [formData, setFormData] = useState<LoginCredentials>({
//     email: '',
//     password: '',
//   })
//   const [formErrors, setFormErrors] = useState<Record<string, string>>({})

//   const from = (location.state as any)?.from?.pathname || '/provider-search'

//   const validationRules = {
//     email: [
//       createValidationRules.required('Email is required'),
//       createValidationRules.email(),
//     ],
//     password: [createValidationRules.required('Password is required')],
//   }

//   const handleInputChange =
//     (field: keyof LoginCredentials) =>
//     (event: React.ChangeEvent<HTMLInputElement>) => {
//       setFormData(prev => ({
//         ...prev,
//         [field]: event.target.value,
//       }))

//       // Clear field error when user starts typing
//       if (formErrors[field]) {
//         setFormErrors(prev => ({
//           ...prev,
//           [field]: '',
//         }))
//       }
//     }

//   const handleSubmit = async (event: React.FormEvent) => {
//     event.preventDefault()

//     // Validate form
//     const errors = validateForm(formData, validationRules)
//     if (Object.keys(errors).length > 0) {
//       setFormErrors(errors)
//       return
//     }

//     dispatch(setLoading(true))
//     dispatch(setError(null))

//     try {
//       const response = await MockAuthService.login(formData)
//       dispatch(login({ user: response.user, token: response.token }))

//       dispatch(
//         addNotification({
//           type: 'success',
//           title: 'Welcome back!',
//           message: `Hello ${response.user.firstName}, you have successfully logged in.`,
//         })
//       )

//       navigate(from, { replace: true })
//     } catch (error: any) {
//       console.error('Login failed:', error)
//       dispatch(setError(error.message || 'Login failed. Please try again.'))
//     } finally {
//       dispatch(setLoading(false))
//     }
//   }

//   const handleLoginClick = () => {
//     navigate('/provider-login')
//   }

//   const handleCreateAccountClick = () => {
//     navigate('/patient-create-account')
//   }

//   return (
//     <Box
//       sx={{
//         minHeight: '100vh',
//         bgcolor: 'grey.50',
//         display: 'flex',
//         flexDirection: 'column',
//       }}
//     >
//       {/* Header */}
//       <SignupHeader />

//       {/* Main Content */}
//       <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
//         {/* Left Section - Image */}
//         <Zoom in timeout={600} style={{ transitionDelay: '150ms' }}>
//           <Box
//             sx={{
//               flex: 1,
//               position: 'relative',
//               display: { xs: 'none', md: 'block' },
//               overflow: 'hidden',
//               bgcolor: 'grey.50',
//               paddingLeft: '6rem',
//             //   paddingTop: '2rem',
//             }}
//           >
//             <Box
//               component="img"
//               src="/PatientLoginPoster.png"
//               alt="Therapist login illustration"
//               sx={{
//                 padding: "8%",
//                 width: '90vw',
//                 height: '90vh',
//                 objectFit: 'cover',
//                 objectPosition: 'center',
//                 transition: 'transform 0.3s ease',
//                 '&:hover': {
//                   transform: 'scale(1.02)',
//                 },
//               }}
//             />
//           </Box>
//         </Zoom>

//         {/* Right Section - Login Form */}
//         <Slide
//           direction="left"
//           in
//           timeout={500}
//           style={{ transitionDelay: '250ms' }}
//         >
//           <Box
//             sx={{
//               flex: 1,
//               display: 'flex',
//               flexDirection: 'column',
//               justifyContent: 'center',
//               alignItems: 'center',
//               px: { xs: 3, sm: 4, md: 6, lg: 8 },
//               py: { xs: 4, sm: 6 },
//               bgcolor: 'grey.50',
//             }}
//           >
//             {/* Login Form Card */}
//             <Grow in timeout={600} style={{ transitionDelay: '350ms' }}>
//               <Paper
//                 elevation={2}
//                 sx={{
//                   p: { xs: 4, sm: 5, md: 6 },
//                   borderRadius: 4,
//                   width: '100%',
//                   maxWidth: { xs: '100%', sm: '450px', md: '500px' },
//                   boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
//                   transition: 'box-shadow 0.3s ease, transform 0.3s ease',
//                   '&:hover': {
//                     boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
//                     transform: 'translateY(-2px)',
//                   },
//                 }}
//               >
//                 {/* Header */}
//                 <Fade in timeout={500} style={{ transitionDelay: '450ms' }}>
//                   <Box sx={{ textAlign: 'center', mb: 4 }}>
//                     <Typography
//                       variant="h4"
//                       sx={{
//                         fontWeight: 400,
//                         color: 'grey.800',
//                         mb: 1,
//                         fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
//                       }}
//                     >
//                       Welcome back!
//                     </Typography>
//                     <Typography
//                       variant="h6"
//                       sx={{
//                         color: 'grey.600',
//                         fontWeight: 400,
//                         fontSize: { xs: '0.8rem', sm: '1rem' },
//                       }}
//                     >
//                       Enter your Credentials to access your Patient Portal
//                     </Typography>
//                   </Box>
//                 </Fade>

//                 {/* Error Display */}
//                 {error && (
//                   <Fade in timeout={400} style={{ transitionDelay: '500ms' }}>
//                     <Alert severity="error" sx={{ mb: 3 }}>
//                       {error}
//                     </Alert>
//                   </Fade>
//                 )}

//                 {/* Login Form */}
//                 <Box component="form" onSubmit={handleSubmit}>
//                   {/* Email Field */}
//                   <Fade in timeout={400} style={{ transitionDelay: '550ms' }}>
//                     <Box sx={{ mb: 3 }}>
//                       <Typography
//                         variant="body2"
//                         sx={{
//                           color: 'grey.700',
//                           mb: 1,
//                           fontWeight: 500,
//                           fontSize: '0.875rem',
//                         }}
//                       >
//                         Email
//                       </Typography>
//                       <TextField
//                         fullWidth
//                         type="email"
//                         value={formData.email}
//                         onChange={handleInputChange('email')}
//                         placeholder="Enter email"
//                         required
//                         error={!!formErrors.email}
//                         helperText={formErrors.email}
//                         disabled={isLoading}
//                         sx={{
//                           '& .MuiOutlinedInput-root': {
//                             bgcolor: 'white',
//                             borderRadius: 2,
//                             transition: 'all 0.3s ease',
//                             '& fieldset': {
//                               borderColor: 'grey.300',
//                             },
//                             '&:hover fieldset': {
//                               borderColor: 'grey.400',
//                               transform: 'scale(1.02)',
//                             },
//                             '&.Mui-focused fieldset': {
//                               borderColor: '#3BAE78',
//                               borderWidth: 2,
//                             },
//                           },
//                           '& .MuiInputBase-input': {
//                             py: 1.5,
//                             fontSize: '0.875rem',
//                           },
//                         }}
//                       />
//                     </Box>
//                   </Fade>

//                   {/* Password Field */}
//                   <Fade in timeout={400} style={{ transitionDelay: '650ms' }}>
//                     <Box sx={{ mb: 2 }}>
//                       <Typography
//                         variant="body2"
//                         sx={{
//                           color: 'grey.700',
//                           mb: 1,
//                           fontWeight: 500,
//                           fontSize: '0.875rem',
//                         }}
//                       >
//                         Password
//                       </Typography>
//                       <TextField
//                         fullWidth
//                         type={showPassword ? 'text' : 'password'}
//                         value={formData.password}
//                         onChange={handleInputChange('password')}
//                         placeholder="Enter password"
//                         required
//                         error={!!formErrors.password}
//                         helperText={formErrors.password}
//                         disabled={isLoading}
//                         sx={{
//                           '& .MuiOutlinedInput-root': {
//                             bgcolor: 'white',
//                             borderRadius: 2,
//                             transition: 'all 0.3s ease',
//                             '& fieldset': {
//                               borderColor: 'grey.300',
//                             },
//                             '&:hover fieldset': {
//                               borderColor: 'grey.400',
//                               transform: 'scale(1.02)',
//                             },
//                             '&.Mui-focused fieldset': {
//                               borderColor: '#3BAE78',
//                               borderWidth: 2,
//                             },
//                           },
//                           '& .MuiInputBase-input': {
//                             py: 1.5,
//                             fontSize: '0.875rem',
//                           },
//                         }}
//                         InputProps={{
//                           endAdornment: (
//                             <InputAdornment position="end">
//                               <IconButton
//                                 onClick={() => setShowPassword(!showPassword)}
//                                 edge="end"
//                                 disabled={isLoading}
//                                 sx={{
//                                   color: 'grey.400',
//                                   transition: 'transform 0.2s ease',
//                                   '&:hover': {
//                                     transform: 'scale(1.1)',
//                                   },
//                                 }}
//                               >
//                                 {showPassword ? (
//                                   <VisibilityOff />
//                                 ) : (
//                                   <Visibility />
//                                 )}
//                               </IconButton>
//                             </InputAdornment>
//                           ),
//                         }}
//                       />
//                     </Box>
//                   </Fade>

//                   {/* Forgot Password Link */}
//                   <Fade in timeout={400} style={{ transitionDelay: '750ms' }}>
//                     <Box sx={{ textAlign: 'left', mb: 4 }}>
//                       <Link
//                         href="#"
//                         sx={{
//                           color: '#3BAE78',
//                           textDecoration: 'none',
//                           fontSize: '0.875rem',
//                           fontWeight: 500,
//                           transition: 'all 0.3s ease',
//                           '&:hover': {
//                             textDecoration: 'underline',
//                             transform: 'translateX(2px)',
//                           },
//                         }}
//                       >
//                         Forgot password
//                       </Link>
//                     </Box>
//                   </Fade>

//                   {/* Login Button */}
//                   <Fade in timeout={400} style={{ transitionDelay: '850ms' }}>
//                     <Button
//                       type="submit"
//                       fullWidth
//                       variant="contained"
//                       disabled={isLoading}
//                       sx={{
//                         bgcolor: '#3BAE78',
//                         py: 1.5,
//                         fontSize: '1rem',
//                         fontWeight: 600,
//                         borderRadius: 2,
//                         mb: 4,
//                         textTransform: 'none',
//                         transition: 'all 0.3s ease',
//                         '&:hover': {
//                           bgcolor: '#2563a0',
//                           transform: 'translateY(-2px)',
//                           boxShadow: '0 4px 12px rgba(46, 125, 175, 0.3)',
//                         },
//                         '&:disabled': {
//                           bgcolor: 'grey.300',
//                           color: 'grey.500',
//                         },
//                       }}
//                     >
//                       {isLoading ? 'Signing in...' : 'Login'}
//                     </Button>
//                   </Fade>
//                 </Box>

//                 {/* Create Account Link */}
//                 <Fade in timeout={400} style={{ transitionDelay: '850ms' }}>
//                   <Box sx={{ textAlign: 'center' }}>
//                     <Typography
//                       variant="body2"
//                       sx={{
//                         color: 'grey.600',
//                         fontSize: '0.875rem',
//                       }}
//                     >
//                       New to PillarThera?{' '}
//                       <Link
//                         component="button"
//                         onClick={handleCreateAccountClick}
//                         sx={{
//                           color: '#3BAE78',
//                           textDecoration: 'none',
//                           fontWeight: 600,
//                           transition: 'all 0.3s ease',
//                           '&:hover': {
//                             textDecoration: 'underline',
//                             transform: 'translateY(-1px)',
//                           },
//                         }}
//                       >
//                         Create an account
//                       </Link>
//                     </Typography>
//                   </Box>
//                 </Fade>
//               </Paper>
//             </Grow>
//           </Box>
//         </Slide>
//       </Box>
//     </Box>
//   )
// }

// export default PatientLoginPage
